import { EnumHelper } from '@/helpers/enumHelper';
import { AlertService } from '@/services/alert.service';
import { IntegerValidator } from '@/validators/IntegerValidator';
import { formatDate, NgFor, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, LOCALE_ID, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import {
  ErrorBusinessCode,
  PropertySale,
  RealProperty,
  SalesManagementService,
  SellingMandateType,
  SellingStatus
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { CompanyDialogComponent } from '../company-dialog/company-dialog.component';
import { SelectComponent } from '../ui-forms/select/select.component';

import { TooltipDirective } from '@/directives/tooltip/tooltip.directive';
import { UrlValidator } from '@/validators/UrlValidator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';
import { InputComponent } from '../ui-forms/input/input.component';

export class SaleUpdateDialogData {
  prop: RealProperty;
  edition? = false;
}

@Component({
  selector: 'app-sale-update-dialog',
  templateUrl: './sale-update-dialog.component.html',
  styleUrls: ['./sale-update-dialog.component.scss'],
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgTemplateOutlet,
    MatDialogModule,
    FormsModule,
    ReactiveFormsModule,
    SelectComponent,
    InputComponent,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    NgFor,
    TooltipDirective
  ]
})
export class SaleUpdateDialogComponent implements OnInit, OnDestroy {
  sub = new Subscription();
  actionSub = new Subscription();
  dialogTitle: string;
  isUpdate: boolean;
  isLoading = false;

  sale: PropertySale;
  mandateTypeList: SellingMandateType[] = EnumHelper.getApiEnumList(SellingMandateType);

  get form_promotionalText() {
    return this.form.get('promotionalText') as FormControl<string>;
  }
  get form_links() {
    return this.form.get('links') as FormArray<UntypedFormGroup>;
  }
  get form_price() {
    return this.form.get('price') as FormControl<number>;
  }
  get form_mandateType() {
    return this.form.get('mandateType') as FormControl<string>;
  }
  get form_dateOnSale() {
    return this.form.get('dateOnSale') as FormControl<string>;
  }
  get form_dateOffer() {
    return this.form.get('dateOffer') as FormControl<string>;
  }
  get form_dateAgreement() {
    return this.form.get('dateAgreement') as FormControl<string>;
  }
  get form_dateBankLoanApplication() {
    return this.form.get('dateBankLoanApplication') as FormControl<string>;
  }
  get form_dateBankLoanObtaining() {
    return this.form.get('dateBankLoanObtaining') as FormControl<string>;
  }
  get form_dateSigning() {
    return this.form.get('dateSigning') as FormControl<string>;
  }

  form = this.fb.group({
    status: [{ value: SellingStatus.SELLING, disabled: true }],
    promotionalText: [null],
    links: this.fb.array([]),
    price: [null, [Validators.required, Validators.min(1), IntegerValidator]],
    mandateType: [null],
    dateOnSale: [formatDate(new Date(), 'yyyy-MM-dd', this.locale), [Validators.required]],
    dateOffer: [null],
    dateAgreement: [null],
    dateBankLoanApplication: [null],
    dateBankLoanObtaining: [null],
    dateSigning: [null]
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SaleUpdateDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<CompanyDialogComponent>,
    private salesService: SalesManagementService,
    private alertService: AlertService,
    @Inject(LOCALE_ID) public locale: string
  ) {
    this.isUpdate = this.data.edition;
    this.dialogTitle = this.data.edition ? 'saleDialog.titleUpdate' : 'saleDialog.titleCreate';
  }

  ngOnInit(): void {
    if (this.data.prop.onGoingSelling) {
      this.sub = this.salesService.getSale(this.data.prop.onGoingSelling.uid).subscribe((s) => {
        this.sale = s;
        this.initForm();
      });
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.actionSub.unsubscribe();
  }

  initForm() {
    if (this.sale) {
      this.form.patchValue(this.sale);
      this.sale.links.forEach(() => this.addLinkFormItem());
      this.form_links.patchValue(this.sale.links);
    }
  }

  createLinkFormItem() {
    return this.fb.group({
      name: [null, [Validators.required, Validators.maxLength(50)]],
      url: [null, [Validators.required, UrlValidator]]
    });
  }

  addLinkFormItem(): void {
    this.form_links.push(this.createLinkFormItem());
  }

  removeLinkFormItem(i: number) {
    this.form_links.removeAt(i);
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      if (this.isUpdate) {
        this.update();
      } else {
        this.create();
      }
    }
  }

  create() {
    this.isLoading = true;
    const body = this.getCreateOrUpdateBody();
    this.actionSub = this.salesService.addPropertySale(this.data.prop.uid, body).subscribe({
      next: (uid: string) => {
        this.alertService.success('Processus de vente créé');
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.manageDateErrors(err);
        this.isLoading = false;
      }
    });
  }

  update() {
    this.isLoading = true;
    const body = this.getCreateOrUpdateBody();
    this.actionSub = this.salesService.updateSale(this.sale.uid, body).subscribe({
      next: () => {
        this.alertService.success('Processus de vente mis à jour');
        this.dialogRef.close(true);
      },
      error: (err: HttpErrorResponse) => {
        this.manageDateErrors(err);
        this.isLoading = false;
      }
    });
  }

  manageDateErrors(err: HttpErrorResponse) {
    if (err.error && err.error.businessCode) {
      switch (err.error.businessCode) {
        case ErrorBusinessCode.INVALID_DATE:
          // date field is the first term
          const dateField = (err.error.details as string).split(' ')[0];
          // parse the date between brackets
          const afterDate = formatDate(
            new Date((err.error.details as string).split('(')[1].replace(')', '')),
            'dd-MM-yyyy',
            this.locale
          );
          const dateError = { invalidDateAfter: afterDate };
          switch (dateField) {
            case 'dateOffer':
              this.form_dateOffer.setErrors(dateError);
              break;
            case 'dateAgreement':
              this.form_dateAgreement.setErrors(dateError);
              break;
            case 'dateBankLoanApplication':
              this.form_dateBankLoanApplication.setErrors(dateError);
              break;
            case 'dateBankLoanObtaining':
              this.form_dateBankLoanObtaining.setErrors(dateError);
              break;
            case 'dateSigning':
              this.form_dateSigning.setErrors(dateError);
              break;
          }
          break;
      }
    }
  }

  getCreateOrUpdateBody(): PropertySale {
    const sale: PropertySale = { ...this.form.value };
    return sale;
  }

  onNoClick(): void {
    this.dialogRef.close(false);
  }
}
